// Login.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  createNewClient,
  sendConfidentialityDisclaimer,
  updateClient,
  getStaffSMSList,
} from "../helpers/client";
import { useAuth } from "../AuthContext";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  increment, // ✅ added back for questionnaire counters
} from "firebase/firestore";
import { db } from "../firebase";
import JMJButton from "../components/JMJButton";
import WhiteJMJLogo from "../components/WhiteJMJLogo";
import LanguageToggle from "../components/LanguageToggle";
import Navbar from "../components/Navbar";
import Phonebar from "../components/Phonebar";
import { sendTextMessage } from "../Helpers";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { getAvailableTimes, getFilteredLocations } from "../helpers/availability";
import { formatDateToSlashes } from "../helpers/datesAndTimes";
import { getLocationById } from "../helpers/location";

const Login = () => {
  const { login } = useAuth();
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [clientType, setClientType] = useState("");
  const [questionnaireStep, setQuestionnaireStep] = useState(0);

  // ✅ stores questionnaire outcome (no / yes / unsure_no / unsure_yes)
  const [questionnaireChoice, setQuestionnaireChoice] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [dob, setDob] = useState("");
  const [menstrual, setMenstrual] = useState("");
  const [notes, setNotes] = useState("");

  const [showExistingClientModal, setShowExistingClientModal] = useState(false);

  // verification + sms
  const [pendingVerificationToken, setPendingVerificationToken] = useState("");
  const [pendingFormattedPhone, setPendingFormattedPhone] = useState("");
  const [sendTextError, setSendTextError] = useState("");
  const [isSendingText, setIsSendingText] = useState(false);
  const sendTextOnceRef = useRef(false);

  // ---- New client booking state ----
  const [selectedDate, setSelectedDate] = useState(""); // YYYY-MM-DD
  const [selectedLocation, setSelectedLocation] = useState("");
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");

  const [appointments, setAppointments] = useState([]);
  const [closures, setClosures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [noLanguages, setNoLanguages] = useState([]);
  const [isLoadingNewClientData, setIsLoadingNewClientData] = useState(false);

  const phonePrivacyText =
    selectedLanguage === "Spanish"
      ? "Pedimos su número de teléfono para confirmar su cita y enviar recordatorios. Su información se mantiene privada y confidencial."
      : "We ask for your phone number so we can confirm your appointment and send reminders. Your information is kept private and confidential.";

  // Date objects for DatePicker
  const todayObj = new Date();
  const tenYearsAgoObj = new Date();
  tenYearsAgoObj.setFullYear(todayObj.getFullYear() - 10);

  const nineMonthsAgoObj = new Date();
  nineMonthsAgoObj.setMonth(nineMonthsAgoObj.getMonth() - 9);

  const generateVerificationToken = () => {
    const arr = new Uint8Array(32);
    crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
  };

  const normalizeName = (s) => {
    if (!s) return "";
    const trimmed = s.trim().replace(/\s+/g, " ");
    if (!trimmed) return "";

    return trimmed
      .split(" ")
      .map((word) =>
        word
          .split("-")
          .map((part) => {
            if (!part) return "";
            const lower = part.toLowerCase();
            return lower.charAt(0).toUpperCase() + lower.slice(1);
          })
          .join("-")
      )
      .join(" ");
  };

  const idsToAddresses = {
    0: "JMJ Orlando Clinic",
    1: "JMJ Kissimmee Clinic",
  };

  const formatHour = (hour) => {
    const h = parseInt(hour, 10);
    if (Number.isNaN(h)) return "";
    const display = h % 12 === 0 ? 12 : h % 12;
    return `${display}:00 ${h >= 12 ? "PM" : "AM"}`;
  };

  const sendStaffFacingNotification = async (message) => {
    try {
      const groupDoc = await getDoc(doc(db, "settings", "SMS_GROUP"));
      const group = groupDoc.exists()
        ? (groupDoc.data()?.group || "").toString().toLowerCase()
        : "staff";

      let recipients = [];

      if (group === "staff") {
        const staffUsers = await getStaffSMSList();
        recipients = staffUsers.map((s) => s.phone?.toString()).filter(Boolean);
      } else if (group === "development") {
        const devDoc = await getDoc(doc(db, "settings", "SMS_DEVELOPMENT"));
        const numbers = devDoc.exists() ? devDoc.data()?.numbers || [] : [];
        recipients = Array.isArray(numbers) ? numbers.map((n) => n.toString()) : [];
      } else {
        console.warn(
          `Unknown SMS group "${group}", defaulting to no recipients except admin.`
        );
        recipients = [];
      }

      recipients = Array.from(new Set(recipients.filter(Boolean)));

      const sendPromises = recipients.map((number) =>
        sendTextMessage({
          message,
          yourPhoneNumber: "4073059084",
          recipientPhoneNumber: number,
          senderName: "JMJPC",
        })
      );

      if (group === "staff") {
        sendPromises.push(
          sendTextMessage({
            message,
            yourPhoneNumber: "4073059084",
            recipientPhoneNumber: "4073059084",
            senderName: "JMJPC",
          })
        );
      }

      await Promise.all(sendPromises);
    } catch (err) {
      console.error("Error sending staff-facing notification:", err);
    }
  };

  // ✅ Questionnaire logging (restored)
  const questionnaireLoggedRef = useRef({});
  const bumpQuestionnaire = async (field) => {
    if (questionnaireLoggedRef.current[field]) return; // avoid double-counting
    questionnaireLoggedRef.current[field] = true;

    try {
      const questionnaireRef = doc(db, "settings", "questionnaire");
      await updateDoc(questionnaireRef, {
        [field]: increment(1),
      });
    } catch (err) {
      console.error("Error updating questionnaire counter:", err);
      questionnaireLoggedRef.current[field] = false; // allow retry if failed
    }
  };

  const questionnaireChoiceLoggedRef = useRef(false);
  const logQuestionnaireChoice = async (field) => {
    if (questionnaireChoiceLoggedRef.current) return;
    questionnaireChoiceLoggedRef.current = true;

    try {
      await bumpQuestionnaire(field);

      const formattedPhone = phone.replace(/\D/g, "");
      if (formattedPhone.length === 10) {
        const clientRef = doc(db, "clients", formattedPhone);
        await updateDoc(clientRef, { questionnaire: field });
      }
    } catch (err) {
      console.error("Error logging questionnaire choice:", err);
      questionnaireChoiceLoggedRef.current = false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (clientType === "returning" && phone.replace(/\D/g, "").length === 10) {
        handleLogin();
      } else if (
        clientType === "staff" &&
        phone.replace(/\D/g, "").length === 10 &&
        password.length > 0
      ) {
        handleStaffLogin();
      }
    }
  };

  // date helpers (ISO string for DatePicker storage)
  const toISODate = (d) => {
    if (!d || isNaN(d)) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const fromISODate = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const handleLogin = async () => {
    const formattedPhone = phone.replace(/\D/g, "");
    const clientDocRef = doc(db, "clients", formattedPhone);
    const clientDoc = await getDoc(clientDocRef);

    if (clientDoc.exists()) {
      const clientData = clientDoc.data();

      if (!clientData.firstName || !clientData.lastName) {
        setError(
          selectedLanguage === "English"
            ? "Please call to schedule an appointment."
            : "Por favor, llame para programar una cita."
        );
      } else if (!clientData.verified) {
        setError(
          selectedLanguage === "Spanish"
            ? "Por favor, revise sus mensajes de texto para el enlace de verificación (puede tardar unos minutos en llegar) o llame para solicitar asistencia."
            : "Please check your texts for a verification link (it may take a few minutes to arrive), or call for assistance."
        );
      } else if (clientData.unableToAct) {
        setError(
          selectedLanguage === "English"
            ? "Your access has been restricted."
            : "Tu acceso ha sido restringido."
        );
      } else if (clientData.verified && !clientData.unableToAct) {
        login(formattedPhone, true);
      }
    } else {
      setError(
        selectedLanguage === "English"
          ? "Please call to schedule an appointment."
          : "Por favor, llame para programar una cita."
      );
    }
  };

  const handleStaffLogin = async () => {
    const formattedPhone = phone.replace(/\D/g, "");
    try {
      const clientDocRef = doc(db, "clients", `${formattedPhone}STAFF`);
      const clientDoc = await getDoc(clientDocRef);

      if (clientDoc.exists()) {
        const clientData = clientDoc.data();

        if (!clientData.password) {
          setError(
            selectedLanguage === "English"
              ? "Incorrect password."
              : "Contraseña incorrecta."
          );
          return;
        }

        if (clientData.password !== password) {
          setError(
            selectedLanguage === "English"
              ? "Incorrect password."
              : "Contraseña incorrecta."
          );
          return;
        }

        login(`${formattedPhone}STAFF`, false);
      } else {
        setError(
          selectedLanguage === "English"
            ? "Incorrect password."
            : "Contraseña incorrecta."
        );
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(
        selectedLanguage === "English"
          ? "An error occurred during login."
          : "Ocurrió un error durante el inicio de sesión."
      );
    }
  };

  const sendConfirmationText = async () => {
    if (sendTextOnceRef.current) return;
    sendTextOnceRef.current = true;

    setSendTextError("");
    setIsSendingText(true);

    try {
      const token = pendingVerificationToken;
      const formattedPhone = pendingFormattedPhone;

      const origin = window.location.origin;
      const link = `${origin}/#/verify/${token}`;

      const verificationMessage =
        selectedLanguage === "Spanish"
          ? `JMJPC BOOKING: Por favor verifique su cuenta haciendo clic en el siguiente enlace: ${link}`
          : `JMJPC BOOKING: Please verify your account by clicking the following link: ${link}`;

      await sendConfidentialityDisclaimer(formattedPhone, selectedLanguage);

      await sendTextMessage({
        message: verificationMessage,
        yourPhoneNumber: "4073059084",
        recipientPhoneNumber: formattedPhone,
        senderName: "JMJPC",
      });

      setIsSendingText(false);
    } catch (err) {
      console.error("Error sending confirmation text:", err);
      setSendTextError(
        selectedLanguage === "Spanish"
          ? "Ocurrió un error al enviar el mensaje de texto. Por favor, inténtelo de nuevo."
          : "There was an error sending the text message. Please try again."
      );
      sendTextOnceRef.current = false; // allow retry
      setIsSendingText(false);
    }
  };

  const handleFirstNameChange = (e) => {
    const value = e.target.value;
    if (/^[A-Za-zñÑ\s-]*$/.test(value)) {
      setFirstName(value);
    }
  };

  const handleLastNameChange = (e) => {
    const value = e.target.value;
    if (/^[A-Za-zñÑ\s-]*$/.test(value)) {
      setLastName(value);
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
  };

  const handlePhoneChange = (e) => {
    const input = e.target.value.replace(/\D/g, "");
    if (input.length <= 3) {
      setPhone(input);
    } else if (input.length <= 6) {
      setPhone(`(${input.slice(0, 3)}) ${input.slice(3)}`);
    } else {
      setPhone(`(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(6, 10)}`);
    }

    if (input.replace(/\D/g, "").length === 10) setError("");
  };

  const validEmail = (str) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(str);
  };

  const validateSchedulableDate = (dateStr) => {
    const d = new Date(`${dateStr}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((d - today) / (24 * 60 * 60 * 1000));

    if (diffDays < 0 || diffDays > 5) {
      return selectedLanguage === "Spanish"
        ? "Solo puede programar hasta 5 días en el futuro."
        : "You can only schedule up to 5 days out.";
    }
    if (d.getDay() === 0) {
      return selectedLanguage === "Spanish"
        ? "No hay citas los domingos."
        : "No Sunday appointments are available.";
    }
    return null;
  };

  // ✅ Step map for NEW clients:
  // 0 = phone
  // 1 = Q1 (Yes/Unsure/No)
  // 2 = Q2 (only if Unsure)
  // 3 = details
  // 4 = appointment selection
  // 5 = final confirmation screen (resend)
  // 6 = call-now screen
  const attemptReachStep = async (step) => {
    if (step === 1) {
      const formattedPhone = phone.replace(/\D/g, "");

      if (formattedPhone.length !== 10) {
        setError(
          selectedLanguage === "English"
            ? "Please enter your 10-digit phone number."
            : "Por favor ingrese su número de teléfono de 10 dígitos."
        );
        return;
      }

      const clientDocRef = doc(db, "clients", formattedPhone);
      const clientDoc = await getDoc(clientDocRef);

      if (clientDoc.exists()) {
        const clientData = clientDoc.data();

        if (clientData.unableToAct) {
          setError(
            selectedLanguage === "English"
              ? "Your access has been restricted."
              : "Tu acceso ha sido restringido."
          );
          return;
        }

        if (clientData.verified) {
          setShowExistingClientModal(true);
          return;
        }

        // Existing but unverified — allow them to continue new-client flow
        setQuestionnaireStep(1);
        return;
      }

      await createNewClient(formattedPhone);
      setQuestionnaireStep(1);
      return;
    }

    setQuestionnaireStep(step);
  };

  const saveNewClientDetails = async () => {
    setError("");
    const formattedPhone = phone.replace(/\D/g, "");

    if (formattedPhone.length !== 10) {
      setError(
        selectedLanguage === "Spanish"
          ? "Por favor ingrese su número de teléfono de 10 dígitos."
          : "Please enter your 10-digit phone number."
      );
      return;
    }

    const normalizedFirstName = normalizeName(firstName);
    const normalizedLastName = normalizeName(lastName);

    const dobObj = new Date(dob + "T00:00:00");
    const formattedDob = `${String(dobObj.getMonth() + 1).padStart(2, "0")}/${String(
      dobObj.getDate()
    ).padStart(2, "0")}/${dobObj.getFullYear()}`;

    const menstrualObj = new Date(menstrual + "T00:00:00");
    const formattedMenstrual = `${String(menstrualObj.getMonth() + 1).padStart(
      2,
      "0"
    )}/${String(menstrualObj.getDate()).padStart(2, "0")}/${menstrualObj.getFullYear()}`;

    try {
      await updateClient(formattedPhone, {
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        email,
        language: selectedLanguage,
        dob: formattedDob,
        menstrual: formattedMenstrual,
        notes,
      });

      setFirstName(normalizedFirstName);
      setLastName(normalizedLastName);

      // ✅ go to appointment selection (step 4)
      setQuestionnaireStep(4);

      // reset booking selections
      setSelectedDate("");
      setSelectedLocation("");
      setSelectedTime("");
      setAvailableTimes([]);

      // reset sms state
      setPendingVerificationToken("");
      setPendingFormattedPhone("");
      setSendTextError("");
      setIsSendingText(false);
      sendTextOnceRef.current = false;
    } catch (e) {
      console.error("saveNewClientDetails error:", e);
      setError(
        selectedLanguage === "Spanish"
          ? "Ocurrió un error al guardar su información."
          : "There was an error saving your information."
      );
    }
  };

  const parseMMDDYYYY = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return null;
    const parts = dateStr.split("/");
    if (parts.length !== 3) return null;
    const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10));
    if (!mm || !dd || !yyyy) return null;
    const d = new Date(yyyy, mm - 1, dd);
    return isNaN(d) ? null : d;
  };

  const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const bookAppointmentAndSendVerification = async () => {
    setError("");
    setSendTextError("");

    const formattedPhone = phone.replace(/\D/g, "");
    if (formattedPhone.length !== 10) {
      setError(
        selectedLanguage === "Spanish"
          ? "Por favor ingrese su número de teléfono de 10 dígitos."
          : "Please enter your 10-digit phone number."
      );
      return;
    }

    if (!selectedDate || !selectedLocation || selectedTime === "") {
      setError(
        selectedLanguage === "Spanish"
          ? "Por favor seleccione fecha, ubicación y hora."
          : "Please select a date, location, and time."
      );
      return;
    }

    const dateErr = validateSchedulableDate(selectedDate);
    if (dateErr) {
      setError(dateErr);
      return;
    }

    const locId =
      typeof selectedLocation === "string"
        ? parseInt(selectedLocation, 10)
        : Number(selectedLocation);

    const slotDate = formatDateToSlashes(selectedDate);
    const slotHour = parseInt(selectedTime, 10);

    setIsSendingText(true);

    try {
      const appointmentsRef = collection(db, "appointments");
      const today0 = startOfToday();

      // Block if client already has upcoming active appt
      const activeSnap = await getDocs(
        query(
          appointmentsRef,
          where("client", "==", formattedPhone),
          where("status", "in", [0, 4])
        )
      );

      const hasUpcomingActive = activeSnap.docs.some((d) => {
        const data = d.data();
        const dt = parseMMDDYYYY(data?.date);
        if (!dt) return false;
        dt.setHours(0, 0, 0, 0);
        return dt >= today0;
      });

      if (hasUpcomingActive) {
        setIsSendingText(false);
        setError(
          selectedLanguage === "Spanish"
            ? "Ya tiene una cita programada. Por favor cancele o reprograme antes de programar otra."
            : "You already have an appointment scheduled. Please cancel or reprograme before booking another."
        );
        return;
      }

      // Defensive slot check
      const slotSnap = await getDocs(
        query(
          appointmentsRef,
          where("date", "==", slotDate),
          where("startTime", "==", slotHour),
          where("location", "==", locId),
          where("status", "in", [0, 1, 2, 3, 4])
        )
      );

      if (!slotSnap.empty) {
        setIsSendingText(false);
        setError(
          selectedLanguage === "Spanish"
            ? "Ese horario ya no está disponible. Por favor elija otro."
            : "That time slot is no longer available. Please pick another."
        );
        return;
      }

      // Create appointment (reserve slot)
      const apptRef = await addDoc(collection(db, "appointments"), {
        date: slotDate,
        startTime: slotHour,
        endTime: slotHour + 1,
        location: locId,
        client: formattedPhone,
        clientId: formattedPhone,
        status: 0,
        confirmed: false,
        createdAt: serverTimestamp(),
        rescheduleReason: "",
      });

      // Generate + store token and pending appointment id on client
      const token = generateVerificationToken();
      await updateClient(formattedPhone, {
        verified: false,
        verificationToken: token,
        pendingAppointmentId: apptRef.id,
      });

      // Send verification SMS
      const origin = window.location.origin;
      const link = `${origin}/#/verify/${token}`;

      const verificationMessage =
        selectedLanguage === "Spanish"
          ? `JMJPC BOOKING: Por favor verifique su cuenta haciendo clic en el siguiente enlace: ${link}`
          : `JMJPC BOOKING: Please verify your account by clicking the following link: ${link}`;

      await sendConfidentialityDisclaimer(formattedPhone, selectedLanguage);

      await sendTextMessage({
        message: verificationMessage,
        yourPhoneNumber: "4073059084",
        recipientPhoneNumber: formattedPhone,
        senderName: "JMJPC",
      });

      try {
        const first = firstName || "Client";
        const last = lastName || "";
        const dateStr = slotDate;
        const timeStr = formatHour(slotHour);

        let locName = idsToAddresses[locId] || "Clinic";

        try {
          if (!Number.isNaN(locId)) {
            const loc = await getLocationById(locId);
            locName = loc?.name ?? locName;
          }
        } catch (e) {
          console.warn("getLocationById failed; using fallback", e);
        }

        const detailPart =
          dateStr && timeStr ? ` for ${timeStr} on ${dateStr} at ${locName}` : "";

        await sendStaffFacingNotification(
          `JMJPC BOOKING: ${first} ${last} (${formattedPhone}) scheduled an UNCONFIRMED appointment${detailPart}. They have been sent a verification text.`
        );
      } catch (e) {
        console.error("Failed sending staff-facing signup notification:", e);
      }

      // Save pending info for optional resend
      setPendingVerificationToken(token);
      setPendingFormattedPhone(formattedPhone);
      sendTextOnceRef.current = false;

      setIsSendingText(false);

      // ✅ go to final confirm screen (step 5)
      setQuestionnaireStep(5);
    } catch (e) {
      console.error("bookAppointmentAndSendVerification error:", e);
      setIsSendingText(false);
      setError(
        selectedLanguage === "Spanish"
          ? "Ocurrió un error al programar/enviar el texto. Por favor, inténtelo de nuevo."
          : "There was an error booking/sending the text. Please try again."
      );
    }
  };

  // Load availability data when new client reaches booking / final step
  useEffect(() => {
    const shouldLoad =
      clientType === "new" && (questionnaireStep === 4 || questionnaireStep === 5);
    if (!shouldLoad) return;

    let active = true;

    (async () => {
      try {
        setIsLoadingNewClientData(true);

        const [
          appointmentsSnapshot,
          closuresSnapshot,
          locationsSnapshot,
          noLanguagesSnapshot,
        ] = await Promise.all([
          getDocs(collection(db, "appointments")),
          getDocs(collection(db, "closures")),
          getDocs(collection(db, "locations")),
          getDocs(collection(db, "noLanguages")),
        ]);

        if (!active) return;

        setAppointments(appointmentsSnapshot.docs.map((d) => ({ ...d.data(), id: d.id })));
        setClosures(closuresSnapshot.docs.map((d) => ({ ...d.data(), id: d.id })));
        setLocations(
          locationsSnapshot.docs.map((d) => ({ ...d.data(), id: parseInt(d.id, 10) }))
        );
        setNoLanguages(noLanguagesSnapshot.docs.map((d) => ({ ...d.data(), id: d.id })));
      } catch (e) {
        console.error("Error loading booking data:", e);
      } finally {
        if (active) setIsLoadingNewClientData(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [clientType, questionnaireStep]);

  // Recompute available times / filtered locations
  useEffect(() => {
    const activeAppointments = appointments.filter((a) => a.status !== 5);

    const pseudoClient = {
      language: selectedLanguage,
    };

    setAvailableTimes(
      getAvailableTimes(
        activeAppointments,
        closures,
        noLanguages,
        selectedDate,
        selectedLocation,
        locations,
        pseudoClient
      )
    );
  }, [
    appointments,
    closures,
    noLanguages,
    selectedDate,
    selectedLocation,
    locations,
    selectedLanguage,
  ]);

  useEffect(() => {
    setFilteredLocations(getFilteredLocations(selectedDate, locations));
  }, [selectedDate, locations]);

  return (
    <div className="relative w-screen min-h-screen overflow-hidden">
      {/* Mobile background (cropped/centered doctor) */}
      <img
        src="/StandingDoctorCropped.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-center mt-32 md:hidden pointer-events-none select-none"
      />

      {/* Desktop/tablet background */}
      <img
        src="/StandingDoctor.webp"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-center hidden md:block pointer-events-none select-none"
      />

      {/* Foreground content */}
      <div className="relative z-10">
        <link rel="icon" href="%PUBLIC_URL%/favicon.ico" />
        <Navbar includeLogout={false} language={selectedLanguage} />
        <Phonebar />
        <div className="flex flex-col gap-8 md:flex-row-reverse md:gap-12 items-center justify-center w-full min-h-screen h-auto px-4 py-12 bg-black bg-opacity-50">
          {/* Left side: Flavor text */}
          <div
            className={`text-white text-center mb-8 md:mb-0 md:flex md:flex-col ${
              clientType === ""
                ? "md:items-center md:text-center"
                : " md:items-center md:ml-56"
            }`}
          >
            <h2 className="text-3xl font-bold mb-2">
              <span className="block md:inline-block md:mr-2">
                {selectedLanguage === "English" ? "Your Pregnancy." : "Tu embarazo."}
              </span>
              <span className="block md:inline-block md:mx-1">
                {selectedLanguage === "English" ? "Your Decision." : "Tu decisión."}
              </span>
              <span className="block md:inline-block md:ml-2">
                {selectedLanguage === "English" ? "Our Support." : "Nuestro apoyo."}
              </span>
            </h2>
            <div className="text-lg">
              <p>
                {selectedLanguage === "English"
                  ? "Your health is our highest priority."
                  : "Tu salud es nuestra máxima prioridad."}
              </p>
              <p>
                {selectedLanguage === "English"
                  ? "We provide the facts and support to help you make the best decision for your situation."
                  : "Te brindamos la información y el apoyo necesarios para que puedes tomar la mejor decisión en tu situación."}
              </p>
            </div>

            {clientType === "" && (
              <div className="mt-3 flex flex-row justify-center md:justify-start gap-4 mb-1">
                <JMJButton
                  onClick={() => {
                    setClientType("new");
                    setQuestionnaireStep(0);
                    setQuestionnaireChoice("");
                    setError("");
                  }}
                  color="green"
                  text={selectedLanguage === "English" ? "New Client" : "Nuevo cliente"}
                />
                <JMJButton
                  onClick={() => {
                    setClientType("returning");
                    setError("");
                  }}
                  color="green"
                  text={selectedLanguage === "English" ? "Returning Client" : "Cliente que regresa"}
                />
              </div>
            )}
            <LanguageToggle language={selectedLanguage} setLanguage={setSelectedLanguage} />
          </div>

          {/* Right side: Login form */}
          {clientType !== "" && (
            <div className="p-6 rounded-lg shadow-lg max-w-[500px] w-[90%] md:w-[33%] px-4 bg-JMJ flex flex-col items-center">
              <WhiteJMJLogo className="mb-6" />

              {/* Returning client */}
              {clientType === "returning" && (
                <div className="flex flex-col items-center w-[100%]">
                  <input
                    type="text"
                    value={phone}
                    onChange={handlePhoneChange}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      selectedLanguage === "English"
                        ? "Enter your phone number"
                        : "Introduce tu número de teléfono"
                    }
                    className="p-2 rounded-lg mb-4 w-full text-center"
                    maxLength={14}
                  />
                  <p className="text-xs text-white -mt-2 mb-4 text-center">
                    {phonePrivacyText}
                  </p>
                  {phone.replace(/\D/g, "").length === 10 && (
                    <div className="flex justify-center mt-4">
                      <JMJButton
                        onClick={handleLogin}
                        text={selectedLanguage === "English" ? "Log In" : "Iniciar sesión"}
                      />
                    </div>
                  )}
                  {error && (
                    <p className="text-red-500 bg-white p-2 rounded-lg mt-8 text-center font-bold">
                      {error}
                    </p>
                  )}
                </div>
              )}

              {/* Staff */}
              {clientType === "staff" && (
                <div className="flex flex-col items-center w-[100%]">
                  <input
                    type="text"
                    value={phone}
                    onChange={handlePhoneChange}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      selectedLanguage === "English"
                        ? "Enter your phone number"
                        : "Introduce tu número de teléfono"
                    }
                    className="p-2 rounded-lg mb-4 w-full text-center"
                    maxLength={14}
                  />
                  <input
                    type="password"
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      selectedLanguage === "English"
                        ? "Enter your password"
                        : "Ingresa tu contraseña"
                    }
                    className="p-2 rounded-lg mb-4 w-full text-center"
                    maxLength={50}
                  />
                  {phone.replace(/\D/g, "").length === 10 && password.length > 0 && (
                    <div className="flex justify-center mt-4">
                      <JMJButton
                        onClick={handleStaffLogin}
                        text={selectedLanguage === "English" ? "Log In" : "Iniciar sesión"}
                      />
                    </div>
                  )}
                  {error && (
                    <p className="text-red-500 mt-4 text-center font-bold">{error}</p>
                  )}
                </div>
              )}

              {/* New client flow */}
              {clientType === "new" && (
                <div className="flex flex-col items-center w-[100%]">
                  {/* Step 0: Phone */}
                  {questionnaireStep === 0 && (
                    <div className="flex flex-col items-center w-[100%]">
                      <input
                        type="text"
                        value={phone}
                        onChange={handlePhoneChange}
                        placeholder={
                          selectedLanguage === "English"
                            ? "Enter your phone number"
                            : "Introduce tu número de teléfono"
                        }
                        className="p-2 rounded-lg mb-4 w-full text-center"
                        maxLength={14}
                      />
                      <p className="text-xs text-white -mt-2 mb-4 text-center">
                        {phonePrivacyText}
                      </p>
                      {phone.replace(/\D/g, "").length === 10 && (
                        <div className="flex mt-4 justify-center">
                          <JMJButton
                            onClick={() => attemptReachStep(1)}
                            text={selectedLanguage === "English" ? "Continue" : "Continuar"}
                          />
                        </div>
                      )}
                      {error && (
                        <p className="text-red-500 mt-4 text-center font-bold">{error}</p>
                      )}
                    </div>
                  )}

                  {/* Step 1: Questionnaire Q1 */}
                  {questionnaireStep === 1 && (
                    <div className="flex flex-col justify-center items-center">
                      <p className="text-xl text-center font-bold text-white">
                        {selectedLanguage === "English"
                          ? "How can we help you today?"
                          : "¿Cómo podemos ayudarle hoy?"}
                      </p>
                      <p className="text-md text-center text-white pb-4">
                        {selectedLanguage === "English"
                          ? "Do you intend to continue the pregnancy?"
                          : "¿Tiene intención de continuar con el embarazo?"}
                      </p>
                      <div className="w-[70%] flex flex-col gap-4 justify-center">
                        <JMJButton
                          text="No"
                          onClick={async () => {
                            setQuestionnaireChoice("no");
                            await logQuestionnaireChoice("no");
                            setQuestionnaireStep(3); // details
                          }}
                          color={"white"}
                        />
                        <JMJButton
                          text={selectedLanguage === "English" ? "Unsure" : "No estoy segura"}
                          onClick={() => {
                            setQuestionnaireChoice("unsure");
                            setQuestionnaireStep(2);
                          }}
                          color={"white"}
                        />
                        <JMJButton
                          text={selectedLanguage === "English" ? "Yes" : "Sí"}
                          onClick={async () => {
                            setQuestionnaireChoice("yes");
                            await logQuestionnaireChoice("yes");
                            setQuestionnaireStep(6); // call screen
                          }}
                          color={"white"}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 2: Questionnaire Q2 (Unsure follow-up) */}
                  {questionnaireStep === 2 && (
                    <div className="flex flex-col justify-center items-center">
                      <p className="text-xl text-center font-bold text-white">
                        {selectedLanguage === "English"
                          ? "How can we help you today?"
                          : "¿Cómo podemos ayudarle hoy?"}
                      </p>
                      <p className="text-md text-center text-white pb-4">
                        {selectedLanguage === "English"
                          ? "Are you looking for a medical proof of positivity for your insurance/Medicaid or help finding a doctor?"
                          : "¿Está buscando una prueba médica de positividad para su seguro/Medicaid o ayuda para encontrar un médico?"}
                      </p>
                      <div className="w-[70%] flex flex-col gap-4 justify-center">
                        <JMJButton
                          text={selectedLanguage === "English" ? "Yes" : "Sí"}
                          onClick={async () => {
                            setQuestionnaireChoice("unsure_yes");
                            await logQuestionnaireChoice("unsure_yes");
                            setQuestionnaireStep(6); // call screen
                          }}
                          color={"white"}
                        />
                        <JMJButton
                          text="No"
                          onClick={async () => {
                            setQuestionnaireChoice("unsure_no");
                            await logQuestionnaireChoice("unsure_no");
                            setQuestionnaireStep(3); // details
                          }}
                          color={"white"}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Details (same as your current flow) */}
                  {questionnaireStep === 3 && (
                    <div className="flex flex-col justify-center items-center">
                      <p className="text-xl text-center font-bold text-white">
                        {selectedLanguage === "English"
                          ? "Pregnancy Verification"
                          : "Verificación de embarazo"}
                      </p>
                      <p className="text-md text-center text-white pb-4">
                        {selectedLanguage === "English"
                          ? "Before you make this important decision, we are here to help you determine the viability of your pregnancy. Please provide the following information to continue."
                          : "Antes de tomar esta importante decisión, estamos aquí para ayudarle a determinar la viabilidad de su embarazo. Por favor proporcione la siguiente información para continuar."}
                      </p>

                      <div className="w-full flex flex-col justify-center">
                        <div className="flex flex-row gap-2">
                          <input
                            type="text"
                            value={firstName}
                            onChange={handleFirstNameChange}
                            placeholder={selectedLanguage === "English" ? "First Name" : "Nombre"}
                            className="p-2 rounded-lg mb-4 w-full text-center"
                            maxLength={50}
                          />
                          <input
                            type="text"
                            value={lastName}
                            onChange={handleLastNameChange}
                            placeholder={selectedLanguage === "English" ? "Last Name" : "Apellido"}
                            className="p-2 rounded-lg mb-4 w-full text-center"
                            maxLength={50}
                          />
                        </div>

                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={
                            selectedLanguage === "English" ? "Email (optional)" : "Correo (opcional)"
                          }
                          className="p-2 rounded-lg mb-4 w-full text-center"
                          maxLength={50}
                        />

                        <p className="text-white">
                          {selectedLanguage === "English" ? (
                            <>
                              Preferred Language (<i>Creole unavailable, please call!</i>)
                            </>
                          ) : (
                            <>
                              Idioma preferido (<i>¡Criollo no disponible, por favor llame!</i>)
                            </>
                          )}
                        </p>
                        <select
                          className="p-2 rounded-lg mb-4 w-full text-start"
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                          <option value="English">English</option>
                          <option value="Spanish">Español</option>
                        </select>

                        <p className="text-white">
                          {selectedLanguage === "English" ? "Date of Birth" : "Fecha de nacimiento"}
                        </p>
                        <DatePicker
                          selected={fromISODate(dob)}
                          onChange={(date) => setDob(toISODate(date))}
                          maxDate={tenYearsAgoObj}
                          placeholderText={
                            selectedLanguage === "English" ? "Select date" : "Seleccione fecha"
                          }
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          withPortal
                          shouldCloseOnSelect
                          locale={selectedLanguage === "Spanish" ? "es" : undefined}
                          customInput={
                            <input className="p-2 rounded-lg mb-4 w-full text-center bg-white text-black" />
                          }
                        />

                        <p className="text-white">
                          {selectedLanguage === "English"
                            ? "First Day of Last Menstrual Period"
                            : "Primer día de la última menstruación"}
                        </p>
                        <p className="text-white italic text-sm mb-1">
                          {selectedLanguage === "English"
                            ? "Florida law (F.S. 390.0111) prohibits abortion if the physician determines the gestational age of the child is more than 6 weeks, with exceptions for medical emergencies, fatal fetal abnormalities, or cases of rape, incest, or human trafficking (up to 15 weeks with documentation)."
                            : "La ley de Florida (F.S. 390.0111) prohíbe el aborto si el médico determina que la edad gestacional del bebé es mayor de 6 semanas, con excepciones por emergencias médicas, anomalías fetales fatales o casos de violación, incesto o trata de personas (hasta 15 semanas con documentación)."}
                        </p>

                        <DatePicker
                          selected={fromISODate(menstrual)}
                          onChange={(date) => setMenstrual(toISODate(date))}
                          minDate={nineMonthsAgoObj}
                          maxDate={todayObj}
                          placeholderText={
                            selectedLanguage === "English"
                              ? "Select date (last 9 months)"
                              : "Seleccione fecha (últimos 9 meses)"
                          }
                          withPortal
                          shouldCloseOnSelect
                          locale={selectedLanguage === "Spanish" ? "es" : undefined}
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          customInput={
                            <input className="p-2 rounded-lg mb-4 w-full text-center bg-white text-black" />
                          }
                        />

                        <input
                          className="p-2 rounded-lg w-full text-start"
                          type=""
                          value={notes}
                          maxLength={500}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder={
                            selectedLanguage === "English"
                              ? "Notes for Provider (optional)"
                              : "Notas para el proveedor (opcional)"
                          }
                        />
                      </div>

                      {firstName.length > 0 &&
                        lastName.length > 0 &&
                        (email === "" || validEmail(email)) &&
                        dob !== "" &&
                        menstrual !== "" && (
                          <div className="flex justify-center mt-4">
                            <JMJButton
                              onClick={saveNewClientDetails}
                              text={selectedLanguage === "English" ? "Continue" : "Continuar"}
                            />
                          </div>
                        )}

                      {error && (
                        <p className="text-red-500 mt-4 text-center font-bold">{error}</p>
                      )}
                    </div>
                  )}

                  {/* Step 4: Appointment selection (your current step 2) */}
                  {questionnaireStep === 4 && (
                    <div className="flex flex-col justify-center items-center w-full">
                      <p className="text-xl text-center font-bold text-white">
                        {selectedLanguage === "Spanish" ? "Programar cita" : "Schedule Appointment"}
                      </p>

                      {isLoadingNewClientData && (
                        <div className="flex items-center gap-3 mt-4 text-white">
                          <img
                            src="/hearts.gif"
                            alt="Loading"
                            className="w-14 h-14 object-contain"
                          />
                          <div className="text-sm font-medium">
                            {selectedLanguage === "Spanish"
                              ? "Cargando disponibilidad..."
                              : "Loading availability..."}
                          </div>
                        </div>
                      )}

                      <div className="w-full flex flex-col justify-center mt-4">
                        <label className="text-white">
                          {selectedLanguage === "Spanish" ? "Fecha" : "Date"}:
                          <input
                            className="text-black w-full p-2 border rounded-lg mb-3"
                            type="date"
                            value={selectedDate}
                            onChange={(e) => {
                              setSelectedDate(e.target.value);
                              setSelectedLocation("");
                              setSelectedTime("");
                              setAvailableTimes([]);
                            }}
                            min={new Date().toISOString().split("T")[0]}
                            max={
                              new Date(new Date().setDate(new Date().getDate() + 5))
                                .toISOString()
                                .split("T")[0]
                            }
                          />
                        </label>

                        <label className="text-white">
                          {selectedLanguage === "Spanish" ? "Ubicación" : "Location"}:
                          <select
                            value={selectedLocation}
                            onChange={(e) => {
                              setSelectedLocation(e.target.value);
                              setSelectedTime("");
                            }}
                            className="w-full p-2 border rounded-lg mb-3 text-black"
                          >
                            <option value="">
                              {selectedLanguage === "Spanish"
                                ? "Seleccione ubicación"
                                : "Select Location"}
                            </option>
                            {filteredLocations.map((loc) => (
                              <option key={loc.id} value={loc.id}>
                                {loc.name}
                              </option>
                            ))}
                          </select>
                        </label>

                        {selectedLocation && selectedDate && (
                          <label className="text-white">
                            {selectedLanguage === "Spanish" ? "Hora" : "Time"}:
                            <select
                              value={selectedTime}
                              className="w-full p-2 border rounded-lg mb-3 text-black"
                              onChange={(e) => setSelectedTime(e.target.value)}
                            >
                              <option value="">
                                {selectedLanguage === "Spanish"
                                  ? "Seleccione hora"
                                  : "Select Time"}
                              </option>
                              {availableTimes.map((hour) => (
                                <option key={hour} value={hour}>
                                  {hour % 12 === 0 ? 12 : hour % 12}:00{" "}
                                  {hour < 12 ? "AM" : "PM"}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                      </div>

                      {!!error && (
                        <p className="text-red-500 bg-white p-2 rounded-lg mt-4 text-center font-bold">
                          {error}
                        </p>
                      )}

                      <div className="flex justify-center mt-4">
                        {isSendingText ? (
                          <div className="flex justify-center w-full">
                            <img
                              src="/hearts.gif"
                              alt="Loading"
                              className="w-40 h-40 object-contain"
                            />
                          </div>
                        ) : (
                          <JMJButton
                            onClick={bookAppointmentAndSendVerification}
                            text={
                              selectedLanguage === "Spanish"
                                ? "Confirmar y enviar texto"
                                : "Confirm & Send Text"
                            }
                            color="gray"
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 5: Final confirmation screen (your current step 3) */}
                  {questionnaireStep === 5 && (
                    <div className="flex flex-col justify-center items-center w-full text-white">
                      <p className="text-xl text-center font-bold">
                        {selectedLanguage === "Spanish"
                          ? "Confirme su número"
                          : "Confirm Your Phone Number"}
                      </p>

                      <p className="text-md text-center mt-3">
                        {selectedLanguage === "Spanish"
                          ? "Para completar su cita, le enviamos un mensaje de texto para confirmar su número de teléfono. Por favor haga clic en el enlace del mensaje para confirmar."
                          : "To complete your appointment, we've sent a text to confirm your phone number. Please click the link in the text to confirm."}
                      </p>

                      {sendTextError && (
                        <p className="text-red-500 bg-white p-2 rounded-lg mt-6 text-center font-bold">
                          {sendTextError}
                        </p>
                      )}

                      <div className="mt-6">
                        {isSendingText ? (
                          <div className="flex items-center gap-3">
                            <img
                              src="/hearts.gif"
                              alt="Loading"
                              className="w-14 h-14 object-contain"
                            />
                            <div className="text-sm font-medium">
                              {selectedLanguage === "Spanish" ? "Enviando..." : "Sending..."}
                            </div>
                          </div>
                        ) : (
                          <JMJButton
                            text={selectedLanguage === "Spanish" ? "Reenviar texto" : "Resend Text"}
                            color="white"
                            onClick={sendConfirmationText}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 6: Call Now screen (from old step 4) */}
                  {questionnaireStep === 6 && (
                    <div className="flex flex-col justify-center items-center">
                      <p className="text-xl text-center font-bold text-white">
                        {selectedLanguage === "English"
                          ? "Do you need medical proof of positivity?"
                          : "¿Necesita prueba médica de positividad?"}
                      </p>
                      <p className="text-md text-center text-white pb-4">
                        {selectedLanguage === "English"
                          ? "We can assist you with obtaining a medical proof of positivity document for your insurance/Medicaid and help you find a doctor for prenatal care."
                          : "Podemos ayudarlo a obtener un documento de prueba médica de positividad para su seguro/Medicaid y ayudarlo a encontrar un médico para atención prenatal."}
                      </p>
                      <p className="text-md text-center text-white pb-4">
                        {selectedLanguage === "English"
                          ? "For the best service possible, these appointments should be scheduled in advance. WALK-IN APPOINTMENTS ARE BASED ON AVAILABILITY for this type of pregnancy consultation and are not guaranteed same-day. Please call one of the numbers below to schedule your FREE appointment."
                          : "Para obtener el mejor servicio posible, estas citas deben programarse con anticipación. Las citas sin cita previa están sujetas a disponibilidad para este tipo de consulta de embarazo y no se garantiza que se realicen el mismo día. Llame a uno de los números a continuación para programar su cita GRATUITA."}
                      </p>
                      <p className="text-md text-center text-white pb-4">
                        <span className="font-bold">
                          {selectedLanguage === "English" ? "PLEASE NOTE:" : "TENGA EN CUENTA:"}
                        </span>
                        {selectedLanguage === "English"
                          ? " We typically do NOT provide an ultrasound as part of these appointments, unless our medical staff deems it necessary."
                          : " Por lo general, NO ofrecemos una ecografía como parte de estas citas, a menos que nuestro personal médico lo considere necesario."}
                      </p>
                      <div className="w-full flex flex-row gap-4 justify-center">
                        <div className="flex flex-col items-center">
                          <JMJButton
                            text={
                              selectedLanguage === "English"
                                ? "Call Now - Orlando"
                                : "Llama ahora - Orlando"
                            }
                            onClick={() => (window.location.href = "tel:407-839-0620")}
                            color="white"
                          />
                          <p className="text-sm text-white mt-2 text-center">10 AM - 2 PM (M/W/F)</p>
                        </div>
                        <div className="flex flex-col items-center">
                          <JMJButton
                            text={
                              selectedLanguage === "English"
                                ? "Call Now - Kissimmee"
                                : "Llama ahora - Kissimmee"
                            }
                            onClick={() => (window.location.href = "tel:407-201-5085")}
                            color="white"
                          />
                          <p className="text-sm text-white mt-2 text-center">10 AM - 2 PM (Tu/Th/Sa)</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showExistingClientModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-md text-black">
                    <h2 className="text-2xl font-bold mb-4">
                      {selectedLanguage === "English"
                        ? "Existing Client Found"
                        : "Cliente existente encontrado"}
                    </h2>
                    <p className="mb-4">
                      {selectedLanguage === "English"
                        ? "This phone number is already associated with a client. Please refresh the page and use the Returning Client button to log in."
                        : "Este número de teléfono ya está asociado con un cliente. Actualice la página y use el botón Cliente que regresa para iniciar sesión."}
                    </p>
                    <div className="flex justify-end gap-2">
                      <JMJButton
                        text={selectedLanguage === "English" ? "Refresh Page" : "Actualizar página"}
                        color="green"
                        onClick={() => window.location.reload()}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {clientType === "" && (
          <div className="fixed bottom-4 left-0 right-0 md:right-4 flex justify-center md:justify-end z-50">
            <JMJButton
              onClick={() => {
                setClientType("staff");
                setError("");
                setPhone("");
                setPassword("");
                setQuestionnaireStep(0);
                setQuestionnaireChoice("");
              }}
              color="green"
              text={
                selectedLanguage === "English"
                  ? "Staff Login"
                  : "Inicio de sesión del personal"
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
